import { useContext, Context } from 'react';
import { useIntl } from 'react-intl';
import { compact } from 'lodash';
import {
  TableColumn,
  TableCells,
  TableFilterCells,
  AppSettingsContext,
} from '@eas/common-web';
import { Ticket, KsAppSettings } from '../../../common/models';
import {
  useRegionsAll,
  useProgramsAll,
  useSpecificTargetsAll,
  useActivitiesAll,
  useNoticesAll,
  useProjectPhasesAll,
  useTagsAll,
  useOrganisationTypesAll,
  useRequestersAll,
  useOperatorsAll,
  useTicketStates,
} from '../operator-api';

export function useColumns(): TableColumn<Ticket>[] {
  const intl = useIntl();
  const { settings } = useContext(
    (AppSettingsContext as unknown) as Context<
      AppSettingsContext<KsAppSettings>
    >
  );
  const fields = settings.fields ?? [];

  const fieldColumns = compact(
    fields.map((field, index) => {
      if (field == null || !field.enabled) {
        return undefined;
      }

      return {
        datakey: `field${index + 1}`,
        name: intl.formatMessage({
          id: field.labelId ?? '',
          defaultMessage: field.labelDefault,
        }),
        width: 200,
        CellComponent: TableCells.TextCell,
        sortable: true,
        filterable: true,
      };
    })
  );

  return [
    {
      datakey: 'identifier',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_IDENTIFIER',
        defaultMessage: 'Číslo',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'state',
      sortkey: 'state.name',
      filterkey: 'state.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_STATE',
        defaultMessage: 'Stav',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useTicketStates
      ),
      valueMapper: TableCells.useSelectCellFactory(useTicketStates),
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'resolveDate',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_RESOLVE_DATE',
        defaultMessage: 'Čas odpovědi',
      }),
      width: 150,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'organisationType',
      sortkey: 'organisationType.name',
      filterkey: 'organisationType.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_ORGANISATION_TYPE',
        defaultMessage: 'Typ organizace',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useOrganisationTypesAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'requester.name',
      displaykey: 'requester',
      sortkey: 'requester.name',
      filterkey: 'requester.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_REQUESTER',
        defaultMessage: 'Tazatel',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleAutocompleteCellFactory(
        useRequestersAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'requester.phone',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_REQUESTER_PHONE',
        defaultMessage: 'Telefon tazatele',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'applicantName',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_APPLICANT_NAME',
        defaultMessage: 'Název žadatele',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'applicantIc',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_APPLICANT_IC',
        defaultMessage: 'IČ žadatele',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'owner',
      sortkey: 'owner.name',
      filterkey: 'owner.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_OWNER',
        defaultMessage: 'Řešitel',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleAutocompleteCellFactory(
        useOperatorsAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'consulted',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_CONSULTED',
        defaultMessage: 'Konzultován',
      }),
      width: 130,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'consultants',
      sortkey: 'consultants.concatenated',
      filterkey: 'consultants.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_CONSULTANTS',
        defaultMessage: 'Konzultanti',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleAutocompleteCellFactory(
        useOperatorsAll
      ),
      valueMapper: TableCells.dictionaryArrayColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'projectName',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_PROJECT_NAME',
        defaultMessage: 'Název projektu',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'projectNumber',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_PROJECT_NUMBER',
        defaultMessage: 'Číslo projektu',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'projectPhase',
      sortkey: 'projectPhase.name',
      filterkey: 'projectPhase.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_PROJECT_PHASE',
        defaultMessage: 'Dotaz k fázi projektu',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useProjectPhasesAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'region',
      sortkey: 'region.name',
      filterkey: 'region.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_REGION',
        defaultMessage: 'Kraj',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useRegionsAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'place',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_PLACE',
        defaultMessage: 'Místo',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'program',
      sortkey: 'program.name',
      filterkey: 'program.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_PROGRAM',
        defaultMessage: 'Program',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useProgramsAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'specificTarget',
      sortkey: 'specificTarget.name',
      filterkey: 'specificTarget.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_SPECIFIC_TARGET',
        defaultMessage: 'Specifický cíl',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useSpecificTargetsAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'notice',
      sortkey: 'notice.name',
      filterkey: 'notice.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_NOTICE',
        defaultMessage: 'Výzva',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useNoticesAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'activity',
      sortkey: 'activity.name',
      filterkey: 'activity.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_ACTIVITY',
        defaultMessage: 'Aktivita',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleSelectCellFactory(
        useActivitiesAll
      ),
      valueMapper: TableCells.dictionaryColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'tags',
      sortkey: 'tags.concatenated',
      filterkey: 'tags.id',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_TAGS',
        defaultMessage: 'Klíčová slova',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: TableFilterCells.useFilterMultipleAutocompleteCellFactory(
        useTagsAll
      ),
      valueMapper: TableCells.dictionaryArrayColumnMapper,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'note',
      displaykey: 'plainNote',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_NOTE',
        defaultMessage: 'Poznámka',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'question',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_QUESTION',
        defaultMessage: 'Otázka',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'answer',
      displaykey: 'plainAnswer',
      name: intl.formatMessage({
        id: 'KS_O_TICKETS_COLUMN_ANSWER',
        defaultMessage: 'Odpověď',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
    },
    ...fieldColumns,
  ];
}
